import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Pencil, Search, Star } from "lucide-react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { canPublishListing, listingStatusNote, publishedCount } from "../utils/vertical";

const STATUS_LABELS = { DRAFT: "Draft", ACTIVE: "Active", UNDER_OFFER: "Under offer", CLOSED: "Sold / leased", EXPIRED: "Expired", WITHDRAWN: "Withdrawn" };
const STATUS_TONES = { ACTIVE: "success", UNDER_OFFER: "warning", CLOSED: "neutral", DRAFT: "neutral", WITHDRAWN: "neutral" };
const PURPOSE_LABELS = { SALE: "For sale", RENT: "For rent" };
const visuallyHidden = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };
const FILTERS = [
  { value: "all", label: "All listings" },
  { value: "published", label: "On the website" },
  { value: "unpublished", label: "Not on the website" },
];

function formatPrice(row) {
  if (row.price == null) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: row.currency || "AUD", maximumFractionDigits: 0 }).format(row.price);
  } catch {
    return `${row.currency || ""} ${row.price}`.trim();
  }
}

function statusChip(status) {
  const palette = colors.status[STATUS_TONES[status] || "neutral"];
  return <Chip size="small" label={STATUS_LABELS[status] || status} sx={{ bgcolor: palette.background, color: palette.text, fontWeight: 600 }} />;
}

// Website details for one listing: address, slug, title and description as
// visitors see them, and the order among featured listings.
function ListingDialog({ row, saving, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    public_slug: row.public_slug || "",
    title_override: row.title_override || "",
    description_override: row.description_override || "",
    sort_order: row.sort_order ?? 0,
    hide_address: Boolean(row.hide_address),
  }));
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const slugInvalid = Boolean(form.public_slug.trim()) && !/^[a-z0-9-]+$/i.test(form.public_slug.trim());

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Website details: {row.title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <MDBox>
                <MDTypography variant="button" fontWeight="medium" display="block">Hide the street address</MDTypography>
                <MDTypography variant="caption" color="text">Visitors see the suburb and postcode only. The title and web address leave the street out too.</MDTypography>
              </MDBox>
              <Switch
                checked={form.hide_address}
                onChange={(event) => setForm((current) => ({ ...current, hide_address: event.target.checked }))}
                inputProps={{ "aria-label": "Hide the street address" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Web address"
              value={form.public_slug}
              onChange={set("public_slug")}
              error={slugInvalid}
              InputProps={{ startAdornment: <InputAdornment position="start">/property/</InputAdornment> }}
              helperText={slugInvalid ? "Use letters, numbers and hyphens only." : "Leave blank to make one from the listing."}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Title on the website" value={form.title_override} onChange={set("title_override")} helperText="Leave blank to use the listing's title." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="Description on the website" value={form.description_override} onChange={set("description_override")} helperText="Leave blank to use the listing's description." />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth type="number" label="Sort order" value={form.sort_order} onChange={set("sort_order")} helperText="Lower shows first." />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="text" color="dark" onClick={onClose} disabled={saving}>Cancel</MDButton>
        <MDButton color="info" disabled={saving || slugInvalid} onClick={() => onSave(form)}>{saving ? "Saving…" : "Save"}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

ListingDialog.propTypes = {
  row: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// Real estate sites: CRM listings and whether each one is on this website.
// Publishing is the agency's choice per listing; nothing goes live by default.
function ListingsTab({ listings, saving, setListingEntry }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return listings.filter((row) => {
      if (filter === "published" && !row.is_published) return false;
      if (filter === "unpublished" && row.is_published) return false;
      return !needle || `${row.title} ${row.public_slug || ""} ${row.title_override || ""}`.toLowerCase().includes(needle);
    });
  }, [filter, listings, query]);

  const count = publishedCount(listings);
  const toggle = (row, field, message) => (event) => setListingEntry(row, { [field]: event.target.checked }, message);

  const saveDetails = async (form) => {
    const saved = await setListingEntry(editing, form, "Website details saved");
    if (saved) setEditing(null);
  };

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <MDBox>
          <MDTypography variant="h6">Listings on this website</MDTypography>
          <MDTypography variant="body2" color="text">
            {count} of {listings.length} listing{listings.length === 1 ? "" : "s"} on the website. A listing goes live only when you switch it on.
          </MDTypography>
        </MDBox>
        <MDBox display="flex" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search listings"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} aria-hidden="true" /></InputAdornment> }}
            inputProps={{ "aria-label": "Search listings" }}
          />
          <TextField select size="small" value={filter} onChange={(event) => setFilter(event.target.value)} inputProps={{ "aria-label": "Show" }} sx={{ minWidth: 180 }}>
            {FILTERS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
        </MDBox>
      </MDBox>

      {!listings.length ? (
        <MDTypography variant="body2" color="text">
          There are no listings yet. Add listings in Real Estate, then publish them here.
        </MDTypography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { borderColor: colors.border.default } }}>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Listing</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">On website</TableCell>
                <TableCell align="center">Featured</TableCell>
                <TableCell align="center">Address</TableCell>
                <TableCell align="right"><MDBox component="span" sx={visuallyHidden}>Details</MDBox></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const publishable = canPublishListing(row);
                const note = listingStatusNote(row);
                const publishSwitch = (
                  <Switch
                    checked={Boolean(row.is_published)}
                    disabled={saving || (!publishable && !row.is_published)}
                    onChange={toggle(row, "is_published", null)}
                    inputProps={{ "aria-label": `Show ${row.title} on the website` }}
                  />
                );
                return (
                  <TableRow key={row.listing_id} hover>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="medium" display="block">{row.title_override || row.title}</MDTypography>
                      <MDTypography variant="caption" color="text">
                        {[PURPOSE_LABELS[row.purpose], formatPrice(row), row.public_slug ? `/property/${row.public_slug}` : null].filter(Boolean).join(" · ")}
                      </MDTypography>
                    </TableCell>
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell align="center">
                      {note && !publishable ? <Tooltip title={note}><span>{publishSwitch}</span></Tooltip> : publishSwitch}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={row.is_featured ? "Featured listings show first" : "Feature this listing"}>
                        <IconButton
                          size="small"
                          disabled={saving}
                          onClick={() => setListingEntry(row, { is_featured: !row.is_featured }, row.is_featured ? "No longer featured" : "Featured")}
                          aria-pressed={Boolean(row.is_featured)}
                          aria-label={`Feature ${row.title}`}
                        >
                          <Star size={18} color={row.is_featured ? colors.status.warning.text : colors.text.light} fill={row.is_featured ? colors.status.warning.text : "none"} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <MDTypography variant="caption" color="text">{row.hide_address ? "Hidden" : "Shown"}</MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Website details">
                        <IconButton size="small" disabled={saving} onClick={() => setEditing(row)} aria-label={`Website details for ${row.title}`}>
                          <Pencil size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6}><MDTypography variant="body2" color="text">No listings match.</MDTypography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {editing && <ListingDialog row={editing} saving={saving} onClose={() => setEditing(null)} onSave={saveDetails} />}
    </Card>
  );
}

ListingsTab.propTypes = {
  listings: PropTypes.arrayOf(PropTypes.object).isRequired,
  saving: PropTypes.bool.isRequired,
  setListingEntry: PropTypes.func.isRequired,
};

export default ListingsTab;
